import React, { useState, useLayoutEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { apiClient } from '@/services/api';
import { isOpenedOutsideTournament } from '@/utils/tournamentMatchLinkState';
import { Card } from '@/components/layout';
import { TournamentInfoCard } from '@/components/TournamentInfoCard';
import { CoefficientsCard } from '@/components/CoefficientsCard';
import { PredictionCard } from '@/components/PredictionCard';
import TournamentHolderCoefficientForm from '@/components/TournamentHolderCoefficientForm/index';
import PredictionForm from '@/components/PredictionForm/index';
import Error404 from '@/components/Error404';
import { ContentLayout } from '@/layouts/ContentLayout';
import { TOURNAMENT_ROLES, USER_ROLES, PARTICIPANT_STATUSES } from '@/constants';
import { isCompleted, isPreMatch, STATUS_DISPLAY_TEXT } from '@/constants/matchStatus';
import { isPredictionLocked } from '@/utils/predictionDeadline';
import { MatchDetailContext } from './context';
import type { MatchDetailContextType, Match, LocalTournament } from './context';
import DetailsTab from '@/pages/match-detail/DetailsTab';
import PredictionsTab from '@/pages/match-detail/PredictionsTab';

interface BlockedStateProps {
  tournamentId: string;
  blockReason?: string | null;
}

const BlockedState: React.FC<BlockedStateProps> = ({ tournamentId, blockReason }) => {
  const { t } = useTranslation(['tournament']);

  return (
    <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '60vh' }}>
      <Card className="ls-text-center" style={{ maxWidth: '400px', width: '100%' }}>
        <div className="ls-mb-4">
          <div
            className="ls-flex ls-align-center ls-justify-center"
            style={{ width: '80px', height: '80px', background: 'var(--bg-error)', borderRadius: '50%', margin: '0 auto' }}
          >
            <svg className="theme-text-error" width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        </div>

        <h2 className="ls-h2 ls-mb-2">{t('tournament:youAreBlocked')}</h2>
        <p className="theme-text-secondary ls-mb-4" style={{ fontSize: '13px' }}>
          {t('tournament:blockedInTournamentPredictions')}
        </p>

        {blockReason && (
          <div className="theme-bg-error ls-py-3 ls-px-3 ls-mb-4" style={{ borderRadius: '8px', borderLeft: '4px solid var(--text-error)' }}>
            <div className="ls-flex ls-align-start ls-gap-2">
              <svg className="theme-text-error" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ls-text-left">
                <p className="ls-label theme-text-error ls-mb-1">{t('tournament:reason')}</p>
                <p className="theme-text-error" style={{ fontSize: '12px' }}>{blockReason}</p>
              </div>
            </div>
          </div>
        )}

        <Link to={`/tournaments/${tournamentId}`} className="ls-btn" style={{ width: '100%' }}>
          {t('tournament:backToTournament')}
        </Link>
      </Card>
    </div>
  );
};

const MatchDetailLayout: React.FC = () => {
  const { tournamentId, matchId } = useParams<{ tournamentId: string; matchId: string }>();
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useBreakpointDown('lg');
  const { t } = useTranslation(['tournament', 'common']);
  const location = useLocation();
  // Decided once per visit: tab switches push entries without the link state.
  const [openedOutsideTournament] = useState(() => isOpenedOutsideTournament(location.state));
  const { setConfig } = usePageConfig();

  const [showCoefficientForm, setShowCoefficientForm] = useState(false);
  const [showPredictionForm, setShowPredictionForm] = useState(false);

  // === DATA FETCHING ===

  const { data: debugMode } = useQuery({
    queryKey: ['debug-mode'],
    queryFn: async () => {
      const response = await apiClient.getDebugMode();
      return response.data?.data;
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const {
    data: tournament,
    isLoading: tournamentLoading,
    error: tournamentError,
  } = useQuery<LocalTournament>({
    queryKey: ['tournament', tournamentId],
    queryFn: async () => {
      const response = await apiClient.getTournament(tournamentId!);
      const tournamentData = response.data?.data || response.data || response;
      if (!tournamentData) {
        throw new Error(`Tournament with ID ${tournamentId} not found`);
      }
      return tournamentData;
    },
    enabled: !!tournamentId && isAuthenticated,
  });

  const {
    data: matchesData,
    isLoading: matchLoading,
    error: matchError,
    refetch: refetchMatch,
  } = useQuery({
    queryKey: ['tournament-match', tournamentId, matchId],
    queryFn: async () => {
      const response = await apiClient.getTournamentMatch(tournamentId!, matchId!);
      const match = response.data?.data;
      if (!match) {
        throw new Error(`Match with ID ${matchId} not found or not accessible`);
      }
      return match;
    },
    enabled: !!tournamentId && !!matchId && isAuthenticated,
  });

  const matchData: Match | undefined = matchesData;

  const {
    data: predictionData,
    isLoading: predictionLoading,
    refetch: refetchPredictionData,
  } = useQuery({
    queryKey: ['user-prediction', tournamentId, matchId],
    queryFn: async () => {
      const response = await apiClient.getMyPredictions(tournamentId!);
      const predictions = response.data?.data?.predictions || [];
      return predictions.find((p: any) => p.match_id.toString() === matchId) || null;
    },
    enabled: !!tournamentId && !!matchId && isAuthenticated && !!matchData?.user_prediction,
  });

  const {
    data: predictionsData,
    isLoading: predictionsLoading,
    error: predictionsError,
  } = useQuery({
    queryKey: ['match-predictions', tournamentId, matchId],
    queryFn: async () => {
      const response = await apiClient.getMatchPredictions(tournamentId!, matchId!);
      return response.data?.data || response.data || response;
    },
    enabled: !!tournamentId && !!matchId && isAuthenticated,
  });

  // Spread the full match.user_prediction object so id, predicted_outcome, and odds_score
  // are preserved. Cherry-picking only the score/points fields breaks subsequent saves:
  // the (userPrediction as any)?.id check below would be undefined, sending the user down
  // the createPrediction path → 409 conflict when a prediction already exists.
  const userPrediction = predictionData || matchData?.user_prediction || null;

  const predictions = predictionsData?.predictions || [];
  const coefficients = predictionsData?.coefficients;
  const predictionsLocked = predictionsData?.predictions_locked === true;
  const predictionsLockTime = predictionsData?.predictions_lock_time ?? null;

  const refetchPredictions = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tournament-match', tournamentId, matchId] });
    await queryClient.invalidateQueries({ queryKey: ['user-prediction', tournamentId, matchId] });
    await queryClient.invalidateQueries({ queryKey: ['my-predictions', tournamentId] });
    await queryClient.invalidateQueries({ queryKey: ['match-predictions', tournamentId, matchId] });
    // Also invalidate the tournament-level matches list so the Matches tab
    // reflects the user's updated prediction when they navigate back to it.
    // PredictionForm centrally invalidates this too, but `refetchPredictions`
    // is exposed via context for other write paths (debug toggles, admin
    // overrides) — keep the invalidation here for completeness.
    await queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'tournament-matches' &&
          String(key[1]) === String(tournamentId)
        );
      },
    });
    await Promise.all([refetchMatch(), refetchPredictionData()]);
  };

  // === PERMISSIONS ===

  const isArchived = !!tournament?.archivedAt;

  const canEditPrediction = (() => {
    if (isArchived) return false;
    if (!matchData) return false;
    const locked = isPredictionLocked(matchData.match_date, tournament?.predictionDeadlineBufferMinutes);
    const isScheduled = isPreMatch(matchData.status) && !locked;
    const isAdmin = user?.role === USER_ROLES.ADMIN;
    if (isAdmin && debugMode?.predictions_debug_mode) return true;
    return isScheduled;
  })();

  const canEditCoefficients = (() => {
    if (isArchived) return false;
    if (!matchData || !tournament?.manualCoefficients) return false;
    const locked = isPredictionLocked(matchData.match_date, tournament?.predictionDeadlineBufferMinutes);
    const isScheduled = isPreMatch(matchData.status) && !locked;
    const isAdmin = user?.role === USER_ROLES.ADMIN;
    const tournamentRole = tournament?.userRole || tournament?.user_role;
    const isHolderOrMaintainer = tournamentRole === TOURNAMENT_ROLES.HOLDER || tournamentRole === TOURNAMENT_ROLES.MAINTAINER;
    const isPublicTournament = tournament?.isPublic || tournament?.is_public;
    if (isAdmin && debugMode?.predictions_debug_mode) return true;
    if (!isScheduled) return false;
    if (isPublicTournament) return isAdmin;
    return isHolderOrMaintainer || isAdmin;
  })();

  // === PAGE CONFIG ===

  const isLoading = matchLoading || tournamentLoading || predictionLoading;
  const hasError = matchError || tournamentError;
  const errorStatus = hasError
    ? (tournamentError as any)?.response?.status || (matchError as any)?.response?.status
    : undefined;
  const is404 = errorStatus === 404 || errorStatus === 400;
  const isBlocked = tournament?.userStatus === PARTICIPANT_STATUSES.BLOCKED;
  const noData = !tournament || !matchData;

  // Tabs must be declared before early returns (Rules of Hooks)
  const tabs = useMemo(() => [
    { component: DetailsTab, path: `/tournaments/${tournamentId}/matches/${matchId}` },
    { component: PredictionsTab, path: `/tournaments/${tournamentId}/matches/${matchId}/predictions` },
  ], [tournamentId, matchId]);

  useLayoutEffect(() => {
    if (isLoading) {
      // Keep navigation visible during load — match-detail tabs (Match/Predictions) don't depend
      // on permissions and are derived purely from URL by RouterNavigationSwiper. Hiding+showing
      // nav between loading and loaded would cause a visible Swiper unmount/mount flicker.
      setConfig({ title: '', headerSolid: true });
    } else if (hasError && is404) {
      setConfig({ title: t('common:notFound'), showBottomNav: false, showNavigation: false, headerSolid: true });
    } else if (hasError) {
      setConfig({ title: t('common:error'), headerSolid: true });
    } else if (isBlocked) {
      setConfig({ title: tournament!.name, showNavigation: false, headerSolid: true });
    } else if (noData) {
      setConfig({ title: t('common:loading'), headerSolid: true });
    } else {
      setConfig({ title: t('tournament:match'), headerSolid: true });
    }
  }, [isLoading, hasError, is404, isBlocked, noData, tournament, tournamentId, setConfig, t]);

  // === LOADING STATE ===
  if (isLoading) {
    return null;
  }

  // === ERROR STATE ===
  if (hasError) {
    if (is404) {
      return <Error404 />;
    }
    return (
      <Card className="theme-bg-error">
        <h2 className="ls-h4 theme-text-error ls-mb-2">{t('common:error')}</h2>
        <p className="theme-text-error" style={{ fontSize: '13px' }}>
          {matchError ? t('tournament:matchNotFound') :
            tournamentError ? t('tournament:tournamentNotFound') :
              t('tournament:unknownError')}
        </p>
        <Link to={`/tournaments/${tournamentId}`} className="ls-btn ls-mt-3">
          {t('tournament:backToTournament')}
        </Link>
      </Card>
    );
  }

  if (noData) {
    return (
      <>
        <div className="ls-flex ls-justify-center ls-align-center ls-py-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
        </div>
        <p className="theme-text-secondary ls-text-center">{t('common:loadingData')}</p>
      </>
    );
  }

  // === BLOCKED STATE ===
  if (isBlocked) {
    return <BlockedState tournamentId={tournamentId!} blockReason={tournament!.userBlockReason} />;
  }

  // === DERIVED DATA ===
  const match: Match = { ...matchData, tournament_name: tournament.name };
  const isFinished = isCompleted(match.status);
  const matchDate = new Date(match.match_date);

  // Inline save handlers
  const handleInlineCoefficientSave = async (team1Coef: number, drawCoef: number, team2Coef: number) => {
    if (!canEditCoefficients) return;
    await apiClient.updateMatchCoefficients(tournamentId!, String(match.id), {
      team1_win_coefficient: team1Coef,
      draw_coefficient: drawCoef,
      team2_win_coefficient: team2Coef,
    });
    await refetchMatch();
  };

  const handleInlinePredictionSave = async (scoreTeam1: number, scoreTeam2: number, oddsScore?: number) => {
    if (!canEditPrediction) return;
    const getOutcome = (s1: number, s2: number) => {
      if (s1 > s2) return 'team1_win';
      if (s1 < s2) return 'team2_win';
      return 'draw';
    };
    const data: any = {
      match_id: match.id,
      tournament_id: parseInt(tournamentId!),
      predicted_score_team1: scoreTeam1,
      predicted_score_team2: scoreTeam2,
      predicted_outcome: getOutcome(scoreTeam1, scoreTeam2),
    };
    if (tournament?.manualCoefficients && oddsScore !== undefined) {
      data.odds_score = oddsScore;
    }
    if ((userPrediction as any)?.id) {
      await apiClient.updatePrediction(String((userPrediction as any).id), data);
    } else {
      await apiClient.createPrediction(data);
    }
    await refetchPredictions();
  };

  const getOutcomeCoefficient = () => {
    if (!userPrediction) return undefined;
    const outcome = (userPrediction as any).predicted_outcome;
    if (outcome === 'team1_win') return match.team1_win_coefficient;
    if (outcome === 'team2_win') return match.team2_win_coefficient;
    return match.draw_coefficient;
  };

  // === SIDEBAR ===
  const sidebar = (
    <>
      <TournamentInfoCard
        logoUrl={tournament.leagueLogo || tournament.league?.logo_url}
        logoAlt={tournament.leagueName || tournament.league?.name || tournament.name}
        title={tournament.name}
        subtitle={matchDate.toLocaleDateString('ru-RU')}
        stats={[
          {
            label: STATUS_DISPLAY_TEXT[match.status] || match.status,
            value: matchDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
          },
          ...(isCompleted(match.status) && match.score_team1 !== undefined && match.score_team2 !== undefined
            ? [{ label: 'Score', value: `${match.score_team1} : ${match.score_team2}` }]
            : []
          ),
        ]}
        statLayout="row"
        collapsibleOnMobile={true}
      />

      <CoefficientsCard
        team1Name={match.team1}
        team2Name={match.team2}
        team1Coefficient={match.team1_win_coefficient}
        drawCoefficient={match.draw_coefficient}
        team2Coefficient={match.team2_win_coefficient}
        source={match.coefficients_source}
        team1Bookmaker={match.team1_bookmaker}
        drawBookmaker={match.draw_bookmaker}
        team2Bookmaker={match.team2_bookmaker}
        showEditButton={canEditCoefficients}
        onEditClick={() => setShowCoefficientForm(true)}
        onInlineSave={handleInlineCoefficientSave}
      />

      <PredictionCard
        predictedScoreTeam1={userPrediction?.predicted_score_team1}
        predictedScoreTeam2={userPrediction?.predicted_score_team2}
        predictedOutcome={(userPrediction as any)?.predicted_outcome}
        pointsEarned={isFinished ? userPrediction?.points_earned : undefined}
        outcomeCoefficient={getOutcomeCoefficient()}
        scoreCoefficient={(userPrediction as any)?.odds_score}
        hideOddsInput={!tournament?.manualCoefficients}
        matchId={match.id}
        tournamentId={tournamentId}
        team1Name={match.team1}
        team2Name={match.team2}
        team1LogoUrl={match.team1_logo_url}
        team2LogoUrl={match.team2_logo_url}
        canEdit={canEditPrediction}
        onEditClick={() => setShowPredictionForm(true)}
        onInlineSave={handleInlinePredictionSave}
      />
    </>
  );

  // PredictionForm and TournamentHolderCoefficientForm both render through Popup (portal),
  // so they're rendered alongside ContentLayout (not as mobileOverlay) on every viewport.
  // Using ContentLayout's mobileOverlay would force a sibling-children swap that unmounts
  // the entire tabs tree (DetailsTab + cards) — feels like a page reload to the user.

  // === CONTEXT ===
  const contextValue: MatchDetailContextType = {
    tournament,
    match,
    tournamentId: tournamentId!,
    matchId: matchId!,
    user,
    userPrediction,
    predictions,
    coefficients,
    predictionsLoading,
    predictionsError,
    predictionsLocked,
    predictionsLockTime,
    debugMode,
    canEditPrediction,
    canEditCoefficients,
    isArchived,
    showPredictionForm,
    setShowPredictionForm,
    showCoefficientForm,
    setShowCoefficientForm,
    refetchPredictions,
    refetchMatch,
    isMobile,
    openedOutsideTournament,
  };

  return (
    <MatchDetailContext.Provider value={contextValue}>
      <ContentLayout
        sidebar={sidebar}
        tabs={tabs}
      />

      {/* Forms render as siblings via Popup (portal). Same on mobile and desktop —
          opening or closing them does NOT remount the tabs tree. */}
      {showCoefficientForm && tournament?.manualCoefficients && (
        <TournamentHolderCoefficientForm
          match={match}
          tournament={tournament}
          onClose={() => setShowCoefficientForm(false)}
          onSuccess={() => {
            setShowCoefficientForm(false);
            refetchMatch();
          }}
        />
      )}

      {showPredictionForm && (
        <PredictionForm
          match={match as any}
          tournament={tournament as any}
          tournamentId={parseInt(tournamentId!)}
          prediction={userPrediction as any}
          hideOddsInput={!tournament?.manualCoefficients}
          homeTeamLogo={match.team1_logo_url}
          awayTeamLogo={match.team2_logo_url}
          leagueName={tournament?.league?.name}
          onClose={() => setShowPredictionForm(false)}
          onSuccess={() => {
            setShowPredictionForm(false);
            refetchPredictions();
          }}
        />
      )}
    </MatchDetailContext.Provider>
  );
};

export default MatchDetailLayout;
